import React, { useState, useMemo, useEffect, useCallback } from "react";
import moment from "moment";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetReports, useDeleteReport, useFavoriteReport, useDuplicateReport } from "api/reports/service";
import { useGetTenantUsers } from "api/users/service";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { getActiveColorStyle } from "utils/colorUtils";
import classNames from "classnames";
import ReportsFilters from "./components/ReportsFilters";
import ReportsCards from "./components/ReportsCards";
import ReportsRowActionsMenu from "./components/ReportsRowActionsMenu";
import ReportsFolderSidebar from "./components/ReportsFolderSidebar";
import {
  buildReportFoldersTree,
  findSectionForFolder,
  getReportOwnerMeta,
  getReportFolderName,
  extractFoldersFromReports,
} from "./utils/buildReportFoldersTree";
import { getRecentReports, pushRecentReport } from "utils/recentReports";
import { parseScheduleFromApi } from "../components/Reports/reportScheduleMapper";

const getScheduleIntervalLabel = (t, reportSchedule) => {
  const parsed = parseScheduleFromApi(reportSchedule);
  const frequency = parsed?.scheduleReport?.frequency;

  switch (frequency) {
    case "one_time_only":
      return t("reports.schedule.oneTimeOnly");
    case "every_minutes":
      return `${t("reports.schedule.every")} ${parsed.scheduleReport.custom_minutes} ${t("reports.schedule.minutes")}`;
    case "every_hours":
      return `${t("reports.schedule.every")} ${parsed.scheduleReport.custom_hours} ${t("reports.schedule.hours")}`;
    case "daily":
      return t("reports.schedule.daily");
    case "weekly":
      return t("reports.schedule.weekly");
    case "monthly":
      return t("reports.schedule.monthly");
    case "first_day_of_month":
      return t("reports.schedule.firstDayOfMonth");
    case "last_day_of_month":
      return t("reports.schedule.lastDayOfMonth");
    default:
      return "-";
  }
};

const formatScheduleNextRun = (value, t) =>
  value ? moment(value).format("MMM D YYYY h:mm A") : t("reports.schedule.notScheduled");

const resolveTabForNode = (node, foldersTree) => {
  if (node?.tab) return node.tab;
  if (node?.type === "recent-report") return node.reportType || null;
  const section = findSectionForFolder(foldersTree, node?.id);
  return section?.tab || null;
};

const Reports = ({ activeColor = "blue", account, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Modal states
  const [deleteModal, setDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  // Mutations
  const deleteReportMutation = useDeleteReport();
  const favoriteReportMutation = useFavoriteReport();
  const duplicateReportMutation = useDuplicateReport();

  // Tab state management - URL'den tab parametresini al, yoksa ilk mevcut tab kullan
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) return tabFromUrl;

    const tabFromState = location.state?.reportsTab;
    if (tabFromState) return tabFromState;

    // Permission-based default tab selection
    if (account?.is_report_admin) return 'custom';
    if (account?.custom_report_access !== 'cannot') return 'custom';
    if (account?.standart_report_access !== 'cannot') return 'standard';
    return 'favorites';
  });

  const [selectedFolderId, setSelectedFolderId] = useState(() => {
    const folderFromUrl = searchParams.get("folder");
    if (folderFromUrl) return folderFromUrl;
    const tab = searchParams.get("tab")
      || location.state?.reportsTab
      || (account?.is_report_admin || account?.custom_report_access !== "cannot"
        ? "custom"
        : account?.standart_report_access !== "cannot"
          ? "standard"
          : "favorites");
    return `${tab}-root`;
  });
  const [folderPanelCollapsed, setFolderPanelCollapsed] = useState(false);

  // URL değiştiğinde tab/folder güncelle
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
    const folderFromUrl = searchParams.get("folder");
    if (folderFromUrl) {
      setSelectedFolderId(folderFromUrl);
    } else if (tabFromUrl) {
      setSelectedFolderId(`${tabFromUrl}-root`);
    }
  }, [searchParams]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1); // ReactTable is 0-indexed
  const [pageSize, setPageSize] = useState(20);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const isTheme2 = shellThemeVariant === "2";

  const selectedUserIds = useMemo(() => {
    return selectedUser?.map(user => user.value) || [];
  }, [selectedUser]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const allowedTabs = useMemo(() => {
    const tabs = ["favorites"];
    const isReportAdmin = account?.is_report_admin;
    const hasStandardAccess = isReportAdmin || account?.standart_report_access !== "cannot";
    const hasCustomAccess = isReportAdmin || account?.custom_report_access !== "cannot";
    if (hasStandardAccess) tabs.push("standard");
    if (hasCustomAccess) tabs.push("custom");
    if (hasCustomAccess || hasStandardAccess) tabs.push("schedule");
    return tabs;
  }, [account]);

  const { data: reportsData, isLoading } = useGetReports(
    activeTab,
    selectedUserIds,
    debouncedSearch,
    page,
    pageSize
  );
  const { data: usersData } = useGetTenantUsers(1, 100);

  const { data: customTreeSource } = useGetReports(
    "custom",
    [],
    "",
    1,
    500,
    { enabled: allowedTabs.includes("custom") }
  );
  const { data: standardTreeSource } = useGetReports(
    "standard",
    [],
    "",
    1,
    500,
    { enabled: allowedTabs.includes("standard") }
  );

  const recentUserKey = account?.id || account?.user?.id || account?.email || "anon";
  const [recentReports, setRecentReports] = useState(() => getRecentReports(recentUserKey));

  useEffect(() => {
    setRecentReports(getRecentReports(recentUserKey));
  }, [recentUserKey]);

  const customReports = customTreeSource?.results || [];
  const folderEntities = useMemo(
    () => extractFoldersFromReports(customReports),
    [customReports]
  );

  const foldersTree = useMemo(
    () =>
      buildReportFoldersTree({
        allowedTabs,
        customReports,
        standardReports: standardTreeSource?.results || [],
        recentReports,
        folderEntities,
      }),
    [
      allowedTabs,
      customReports,
      standardTreeSource?.results,
      recentReports,
      folderEntities,
    ]
  );

  const selectedTreeNode = useMemo(() => {
    if (!selectedFolderId) return null;
    const walk = (nodes) => {
      for (const node of nodes || []) {
        if (node.id === selectedFolderId) return node;
        const found = walk(node.children);
        if (found) return found;
      }
      return null;
    };
    return walk(foldersTree);
  }, [foldersTree, selectedFolderId]);

  const ReportsTable = isTheme2 ? ReactTable2 : ReactTable;
  const showFilters = account?.is_report_admin || account?.custom_report_access !== "cannot";

  const tableData = useMemo(() => {
    // Folder/owner views must filter the full custom list (tree query), not the
    // paginated table query — otherwise reports outside page 1 look "missing".
    const useTreeSource =
      activeTab === "custom" &&
      selectedTreeNode &&
      (selectedTreeNode.type === "folder" || selectedTreeNode.type === "owner");
    let rows = useTreeSource
      ? [...(customReports || [])]
      : [...(reportsData?.results || [])];

    // Schedule / other section roots → API already scopes the list
    if (!selectedTreeNode || selectedTreeNode.type === "section") {
      return rows;
    }

    if (selectedTreeNode.type === "owner") {
      if (selectedTreeNode.ownerId == null) {
        rows = rows.filter((report) => getReportOwnerMeta(report).isPublic);
      } else {
        const ownerId = String(selectedTreeNode.ownerId);
        rows = rows.filter(
          (report) => String(getReportOwnerMeta(report).ownerId) === ownerId
        );
      }
      return rows;
    }

    if (selectedTreeNode.type === "folder") {
      if (selectedTreeNode.apiFolderId != null) {
        const folderId = String(selectedTreeNode.apiFolderId);
        rows = rows.filter((report) => {
          const rel = report?.report_folder;
          if (rel == null || rel === "") return false;
          if (typeof rel === "object") {
            return (
              String(rel.id) === folderId ||
              String(rel.documentId) === folderId
            );
          }
          return String(rel) === folderId;
        });
      } else {
        const folderName = selectedTreeNode.name;
        rows = rows.filter((report) => {
          const reportFolder = getReportFolderName(report);
          if (selectedTreeNode.ownerId != null) {
            const ownerMeta = getReportOwnerMeta(report);
            if (String(ownerMeta.ownerId) !== String(selectedTreeNode.ownerId)) return false;
          } else if (selectedTreeNode.id?.startsWith("custom-folder-public-")) {
            if (!getReportOwnerMeta(report).isPublic) return false;
          }
          return reportFolder === folderName;
        });
      }
    }

    return rows;
  }, [reportsData?.results, selectedTreeNode, customReports, activeTab]);

  const isClientFilteredView =
    activeTab === "custom" &&
    selectedTreeNode &&
    (selectedTreeNode.type === "folder" || selectedTreeNode.type === "owner");

  const pagedTableData = useMemo(() => {
    if (!isClientFilteredView) return tableData;
    const start = (page - 1) * pageSize;
    return tableData.slice(start, start + pageSize);
  }, [isClientFilteredView, tableData, page, pageSize]);

  const clientPageCount = useMemo(() => {
    if (!isClientFilteredView) return reportsData?.pagination?.pageCount || 1;
    return Math.max(1, Math.ceil(tableData.length / pageSize));
  }, [isClientFilteredView, tableData.length, pageSize, reportsData?.pagination?.pageCount]);

  const clientTotalItems = isClientFilteredView
    ? tableData.length
    : reportsData?.pagination?.total;

  const rememberReport = useCallback(
    (report) => {
      if (!report?.id) return;
      setRecentReports(
        pushRecentReport(recentUserKey, {
          id: report.id,
          name: report.name || `Report ${report.id}`,
          type: report.type || activeTab,
        })
      );
    },
    [recentUserKey, activeTab]
  );

  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch, selectedUser, selectedFolderId]);

  const reportDetailState = useMemo(() => ({ reportsTab: activeTab }), [activeTab]);

  const handleAddReport = useCallback(() => {
    navigate('/reports/add', { state: reportDetailState });
  }, [navigate, reportDetailState]);

  const handleEditReport = useCallback((report) => {
    const reportId = typeof report === "object" ? report?.id : report;
    if (typeof report === "object" && report?.id) rememberReport(report);
    else {
      const row = (reportsData?.results || []).find((r) => String(r.id) === String(reportId));
      if (row) rememberReport(row);
    }
    navigate(`/reports/${reportId}`, { state: reportDetailState });
  }, [navigate, reportDetailState, rememberReport, reportsData?.results]);

  const handleScheduleReport = useCallback((reportId) => {
    const row = (reportsData?.results || []).find((r) => String(r.id) === String(reportId));
    if (row) rememberReport(row);
    navigate(`/reports/${reportId}?tab=SCHEDULE`, { state: reportDetailState });
  }, [navigate, reportDetailState, rememberReport, reportsData?.results]);

  const handleViewReport = useCallback((report) => {
    const reportId = typeof report === "object" ? report?.id : report;
    if (typeof report === "object" && report?.id) rememberReport(report);
    else {
      const row = (reportsData?.results || []).find((r) => String(r.id) === String(reportId));
      if (row) rememberReport(row);
    }
    navigate(`/reports/${reportId}?view=true`, { state: reportDetailState });
  }, [navigate, reportDetailState, rememberReport, reportsData?.results]);

  const handleDeleteReport = (reportId) => {
    setReportToDelete(reportId);
    setDeleteModal(true);
  };

  const handleDuplicateReport = async (reportId) => {
    try {
      await duplicateReportMutation.mutateAsync(reportId);
    } catch (error) {
      console.error('Duplication failed:', error);
    }
  };

  const confirmDelete = async () => {
    if (reportToDelete) {
      try {
        await deleteReportMutation.mutateAsync(reportToDelete);
        setDeleteModal(false);
        setReportToDelete(null);
        // Refresh the reports list
        window.location.reload(); // Simple refresh, could be improved with query invalidation
      } catch (error) {
        console.error('Delete failed:', error);
        // Handle error - could show notification
      }
    }
  };

  const cancelDelete = () => {
    setDeleteModal(false);
    setReportToDelete(null);
  };

  const reportColumns = useMemo(() => [
    {
      Header: t('reports.reportName'),
      accessor: 'name',
      Cell: ({ value }) => {
        if (!value) return '-';
        const displayValue = value.length > 40 ? value.substring(0, 40) + '...' : value;
        return (
          <div
            title={value}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%'
            }}
          >
            {displayValue}
          </div>
        );
      },
      width: activeTab === "schedule" ? '28%' : '35%',
      minWidth: 250,
    },
    ...(activeTab === "schedule"
      ? [
          {
            Header: t("reports.schedule.scheduleName"),
            accessor: "report_schedule.name",
            width: "24%",
            minWidth: 200,
            Cell: ({ row }) => {
              const text = row.original?.report_schedule?.name || "-";
              return (
                <div
                  title={text}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {text}
                </div>
              );
            },
          },
          {
            Header: t("reports.schedule.nextRun"),
            accessor: "report_schedule.next_run_at",
            width: "22%",
            minWidth: 180,
            Cell: ({ row }) => {
              const text = formatScheduleNextRun(
                row.original?.report_schedule?.next_run_at,
                t
              );
              return (
                <div title={text} style={{ whiteSpace: "nowrap" }}>
                  {text}
                </div>
              );
            },
          },
          {
            Header: t("reports.schedule.interval"),
            accessor: "report_schedule.schedule_report.frequency",
            width: "16%",
            minWidth: 160,
            Cell: ({ row }) => {
              const text = getScheduleIntervalLabel(t, row.original?.report_schedule);
              return (
                <div
                  title={text}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {text}
                </div>
              );
            },
          },
        ]
      : [
          {
            Header: t('reports.owner'),
            accessor: 'owner',
            width: '15%',
            maxWidth: 150,
            whiteSpace: 'nowrap',
            Cell: ({ value }) => {
              const text = activeTab === 'standard' ? 'System' : (value?.user_detail?.full_name || '-');
              return (
                <div
                  title={text}
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%'
                  }}
                >
                  {text}
                </div>
              );
            },
          },
          {
            Header: t('reports.description'),
            accessor: 'description',
            width: '25%',
            minWidth: 150,
            Cell: ({ value }) => {
              if (!value) return '-';
              const text = value.charAt(0).toUpperCase() + value.slice(1);
              const displayValue = text.length > 40 ? text.substring(0, 40) + '...' : text;
              return (
                <div
                  title={text}
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%'
                  }}
                >
                  {displayValue}
                </div>
              );
            },
          },
          {
            Header: t('reports.primaryDataSource'),
            accessor: 'primary_data_source',
            width: '15%',
            maxWidth: 150,
            whiteSpace: 'nowrap',
            Cell: ({ value }) => {
              const text = value ? value.charAt(0).toUpperCase() + value.slice(1) : '-';
              return (
                <div
                  title={text}
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%'
                  }}
                >
                  {text}
                </div>
              );
            },
          },
        ]),
    {
      // Header: t('common.actions'),
      accessor: 'actions',
      width: '10%',
      minWidth: 120,
      Cell: ({ row }) => (
        <div className="d-flex justify-content-end align-items-center">
          <Button
            size="sm"
            color="link"
            className="btn-icon btn-link like"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              let hasActiveFilters = false;
              const filtersData = row.original.type === 'standard' ? row.original.parameters : row.original.filters;
              if (filtersData) {
                try {
                  const parsed = typeof filtersData === 'string' ? JSON.parse(filtersData) : filtersData;
                  if (Array.isArray(parsed)) {
                    hasActiveFilters = parsed.some(f => f.is_active === true || f.is_active === "true" || f.is_active === 1);
                  }
                } catch (err) { console.error('Error parsing filters:', err); }
              }

              if (hasActiveFilters) {
                rememberReport(row.original);
                navigate(`/reports/${row.original.id}?generate=true`, { state: reportDetailState });
              } else {
                handleViewReport(row.original);
              }
            }}
          >
            <i className="fas fa-play" style={{ color: activeColorStyle.borderColor }} />
          </Button>
          <Button
            size="sm"
            color="link"
            className="btn-icon btn-link like"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              favoriteReportMutation.mutate(row.original.id);
            }}
          >
            <i className={`${row.original.is_favorite ? 'fas fa-star text-warning' : 'far fa-star text-warning'}`} />
          </Button>
          <div onClick={(e) => { e.stopPropagation(); }}>
            <ReportsRowActionsMenu
              report={row.original}
              onEdit={handleEditReport}
              onSchedule={handleScheduleReport}
              onDuplicate={handleDuplicateReport}
              onDelete={handleDeleteReport}
              duplicatePending={duplicateReportMutation.isPending}
              t={t}
            />
          </div>

        </div >
      ),
    },
  ], [activeTab, t, activeColorStyle, duplicateReportMutation.isPending, reportDetailState, navigate, handleViewReport, favoriteReportMutation, handleEditReport, handleScheduleReport, rememberReport]);

  const handleSelectFolder = useCallback(
    (node) => {
      if (!node) return;

      if (node.type === "recent-report" && node.reportId != null) {
        rememberReport({
          id: node.reportId,
          name: node.name,
          type: node.reportType || activeTab,
        });
        navigate(`/reports/${node.reportId}`, { state: reportDetailState });
        return;
      }

      if (node.id === "recent-root") {
        return;
      }

      const nextTab = resolveTabForNode(node, foldersTree) || activeTab;
      const nextFolderId = node.id;
      setActiveTab(nextTab);
      setSelectedFolderId(nextFolderId);

      // Section roots must show the full tab list — clear owner filter
      if (node.type === "section") {
        setSelectedUser(null);
      } else if (node.type === "owner") {
        if (node.ownerId != null) {
          setSelectedUser([{ value: node.ownerId, label: node.name }]);
        } else {
          setSelectedUser(null);
        }
      } else if (node.type === "folder") {
        // Folder owner_folder ≠ report owner. Auto-applying owners= hides reports
        // that live in this folder but are owned by someone else (shows "No data"
        // until hard refresh clears selectedUser).
        setSelectedUser(null);
      } else {
        setSelectedUser(null);
      }

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("tab", nextTab);
      if (node.type === "section") {
        newSearchParams.delete("folder");
      } else {
        newSearchParams.set("folder", nextFolderId);
      }
      setSearchParams(newSearchParams);
    },
    [activeTab, foldersTree, searchParams, setSearchParams, rememberReport, navigate, reportDetailState]
  );

  const userOptions = useMemo(
    () =>
      usersData?.results?.map((user) => ({
        value: user.id,
        label: user.user_detail?.full_name,
      })) || [],
    [usersData]
  );

  const headerActions = account?.is_report_admin ? (
    <Button
      size="sm"
      style={{ ...activeColorStyle, color: "white" }}
      onClick={handleAddReport}
    >
      {t("reports.addReport")}
    </Button>
  ) : null;

  const filters = (
    <ReportsFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      showFilters={showFilters}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      selectedUser={selectedUser}
      onSelectedUserChange={setSelectedUser}
      userOptions={userOptions}
      actions={headerActions}
    />
  );

  const folderSidebar = (
    <ReportsFolderSidebar
      activeColor={activeColor}
      collapsed={folderPanelCollapsed}
      onCollapsedChange={setFolderPanelCollapsed}
      selectedFolderId={selectedFolderId}
      onSelectFolder={handleSelectFolder}
      allowedTabs={allowedTabs}
      folders={foldersTree}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
    >
      <ReportsTable
        activeColor={activeColor}
        data={pagedTableData}
        columns={reportColumns}
        manualPagination={true}
        pageCount={clientPageCount}
        pageIndex={page - 1}
        totalItems={clientTotalItems}
        pageSize={pageSize}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        className="-striped -highlight sticky-stock-table"
        loading={isLoading}
        loadingText={t("common.loading")}
        noDataText={t("common.noData")}
        onRowClick={(row) => {
          if (row?.is_edit) {
            handleEditReport(row);
          } else {
            handleViewReport(row);
          }
        }}
      />
    </div>
  );

  return (
    <div
      className={classNames("content", "search-reports", {
        "search-reports--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-reports-inset": isTheme2,
      })}
    >
      <ReportsCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
        folderSidebar={folderSidebar}
        folderCollapsed={folderPanelCollapsed}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={cancelDelete} centered>
        <ModalHeader toggle={cancelDelete}>
          {t('reports.deleteReport')}
        </ModalHeader>
        <ModalBody>
          {t('reports.deleteConfirmMessage')}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={cancelDelete}
            disabled={deleteReportMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color="danger"
            onClick={confirmDelete}
            disabled={deleteReportMutation.isPending}
            style={activeColorStyle}
          >
            {deleteReportMutation.isPending ? t('common.deleting') : t('common.delete')}
          </Button>
        </ModalFooter>
      </Modal >
    </div >
  );
};

export default Reports;